/**
 * Name Formatting - Shared Player/Subject Name Helpers
 *
 * Lives in the shared UI package so the frontend renderers and the admin
 * question previews format names identically (same pattern as
 * stat-type-display).
 */

/**
 * Generational suffixes that must not be mistaken for a surname when
 * abbreviating a name (e.g. "Aaron Jones Jr" -> "A. Jones Jr", not "A. Jr").
 * Compared case-insensitively; both bare and period forms are covered.
 */
const NAME_SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v']);

/**
 * Formats a full name as "Initial. Surname" (e.g. "Patrick Mahomes" -> "P. Mahomes").
 * A trailing generational suffix (Jr/Sr/II/III/IV/V) is kept with the surname, so
 * "Aaron Jones Jr" -> "A. Jones Jr" rather than dropping the real surname.
 * Single-word names are returned as-is; empty string returns empty.
 */
export function formatPlayerNameAsInitialSurname(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  /*
   * Sportradar's MMA feed names fighters surname-first — "Brito, Joanderson". Reading that as
   * first-name-first gave "B. Joanderson". Everything before the comma is the surname, so a
   * two-word one ("Dumont Viana, Norma") and a suffix ("Aswell Jr, Michael") stay whole.
   */
  const comma = trimmed.indexOf(',');
  if (comma > 0) {
    const surname = trimmed.slice(0, comma).trim();
    const given = trimmed.slice(comma + 1).trim();
    return given ? `${given.charAt(0).toUpperCase()}. ${surname}` : surname;
  }
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return trimmed;
  const initial = parts[0].charAt(0).toUpperCase();
  const last = parts[parts.length - 1];
  const surname =
    parts.length >= 3 && NAME_SUFFIXES.has(last.toLowerCase())
      ? `${parts[parts.length - 2]} ${last}`
      : last;
  return `${initial}. ${surname}`;
}
